import { useMutation, useQueryClient } from "@tanstack/react-query"
import { notificationApiCalls } from "../api/notificationApi"
import {
  cancelNotificationQueries,
  findCachedNotification,
  invalidateNotificationCaches,
  NotificationMutationContext,
  patchAllLists,
  rollbackNotificationCaches,
  setUnreadCount,
  snapshotNotificationCaches,
} from "./cacheUtils"

/** Optimistic single mark-read: count decrements immediately, rolls back on error. */
export const useMarkRead = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string, NotificationMutationContext>({
    mutationFn: (notificationId) =>
      notificationApiCalls.markRead(notificationId),
    onMutate: async (notificationId) => {
      await cancelNotificationQueries(queryClient)
      const context = snapshotNotificationCaches(queryClient)

      const cached = findCachedNotification(queryClient, notificationId)
      const wasUnread = cached ? cached.readAt === null : true

      if (wasUnread) {
        setUnreadCount(queryClient, (previous) => ({
          ...previous,
          count: Math.max(0, previous.count - 1),
        }))
      }

      const now = new Date().toISOString()
      patchAllLists(queryClient, (item) =>
        item.id === notificationId && item.readAt === null
          ? { ...item, readAt: now }
          : item,
      )

      return context
    },
    onError: (_error, _notificationId, context) => {
      rollbackNotificationCaches(queryClient, context)
    },
    onSettled: () => {
      invalidateNotificationCaches(queryClient)
    },
  })
}

/** Optimistic mark-all-read: badge clears immediately, rolls back on error. */
export const useMarkAllRead = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, void, NotificationMutationContext>({
    mutationFn: () => notificationApiCalls.markAllRead(),
    onMutate: async () => {
      await cancelNotificationQueries(queryClient)
      const context = snapshotNotificationCaches(queryClient)

      setUnreadCount(queryClient, (previous) => ({ ...previous, count: 0 }))

      const now = new Date().toISOString()
      patchAllLists(queryClient, (item) =>
        item.readAt === null ? { ...item, readAt: now } : item,
      )

      return context
    },
    onError: (_error, _variables, context) => {
      rollbackNotificationCaches(queryClient, context)
    },
    onSettled: () => {
      invalidateNotificationCaches(queryClient)
    },
  })
}
